import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle2, Circle, Clock, Users, Calendar, 
  FileCheck, Loader2, ChevronRight, Phone, Mail, 
  TrendingUp, Target, AlertCircle, Sparkles, MessageCircle,
  X, ClipboardList, Camera, MessageSquare, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom CSS animation for pipeline glow effect
const customStyles = `
@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 15px rgba(249, 115, 22, 0.6), 0 0 30px rgba(249, 115, 22, 0.4), 0 0 45px rgba(249, 115, 22, 0.2);
  }
  50% {
    box-shadow: 0 0 25px rgba(249, 115, 22, 0.8), 0 0 50px rgba(249, 115, 22, 0.6), 0 0 75px rgba(249, 115, 22, 0.4);
  }
}

@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-bounce-subtle {
  animation: pulse-glow 2s infinite, bounce-subtle 1s infinite;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = customStyles;
  if (!document.head.querySelector('#pipeline-animation-styles')) {
    styleElement.id = 'pipeline-animation-styles';
    document.head.appendChild(styleElement);
  }
}

// Pipeline stage configuration
const PIPELINE_STAGES = [
  { id: 'captacion', name: 'Captación', icon: '🎯', color: '#f97316' },
  { id: 'registro', name: 'Registro', icon: '📝', color: '#8b5cf6' },
  { id: 'planificacion', name: 'Planificación', icon: '📅', color: '#06b6d4' },
  { id: 'produccion', name: 'Producción', icon: '🎬', color: '#ec4899' },
  { id: 'atencion', name: 'Atención', icon: '💼', color: '#10b981' },
  { id: 'reporte', name: 'Reporte', icon: '📊', color: '#eab308' }
];

// Stage History Modal Component
function StageHistoryModal({ stage, stageConfig, onClose, clientId }) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState({
    checklist: [],
    interactions: [],
    tasks: [],
    pipeline_history: []
  });

  useEffect(() => {
    if (stage && clientId) {
      loadStageHistory();
    }
  }, [stage, clientId]);

  const loadStageHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load all data in parallel
      const [checklistRes, interactionsRes, tasksRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/my-checklist`, { headers }).catch(() => ({ data: { checklist: {} } })),
        axios.get(`${API_URL}/api/portal/my-interactions`, { headers }).catch(() => ({ data: { interactions: [] } })),
        axios.get(`${API_URL}/api/portal/my-production`, { headers }).catch(() => ({ data: { productions: [] } })),
        axios.get(`${API_URL}/api/portal/my-progress`, { headers }).catch(() => ({ data: {} }))
      ]);

      // Get pipeline history from progress
      const pipelineHistory = progressRes.data?.pipeline_history || [];
      
      // Filter checklist items by stage
      const allChecklistItems = checklistRes.data?.checklist?.items || [];
      const stageChecklist = allChecklistItems.filter(item => {
        const itemStage = item.stage?.toLowerCase() || '';
        return itemStage.includes(stage) || stage === 'produccion';
      });
      
      // Get interactions visible to client
      const allInteractions = (interactionsRes.data?.interactions || [])
        .filter(i => i.visible_to_client !== false);
      
      // Get tasks
      const allTasks = tasksRes.data?.productions || [];
      
      // Filter by stage relevance
      const stageTasks = allTasks.filter(t => {
        if (stage === 'produccion') return true;
        if (stage === 'planificacion') return t.status === 'scheduled' || t.status === 'pending';
        if (stage === 'atencion') return t.type?.includes('revision') || t.type?.includes('ajuste');
        if (stage === 'reporte') return t.type?.includes('reporte') || t.status === 'completed';
        return false;
      }).slice(0, 8);

      setHistory({
        checklist: stageChecklist.length > 0 ? stageChecklist : allChecklistItems.slice(0, 5),
        interactions: allInteractions.slice(0, 10),
        tasks: stageTasks,
        pipeline_history: pipelineHistory.filter(h => h.stage === stage || h.to_stage === stage)
      });
    } catch (error) {
      console.error('Error loading stage history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!stage) return null;

  const hasAnyData = history.checklist.length > 0 || 
                     history.interactions.length > 0 || 
                     history.tasks.length > 0 ||
                     history.pipeline_history.length > 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white/65 rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border border-[rgba(0,0,0,0.06)]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[rgba(0,0,0,0.06)] bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
              <span className="text-xl">{stageConfig?.icon}</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1D1D1F]">{stageConfig?.name}</h3>
              <p className="text-xs text-[#AEAEB2]">Gestiones y avances de esta etapa</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/70 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[#AEAEB2]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
              <p className="text-[#AEAEB2] text-sm">Cargando información...</p>
            </div>
          ) : !hasAnyData ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/70 flex items-center justify-center">
                <History className="w-8 h-8 text-[#6E6E73]" />
              </div>
              <h4 className="text-[#1D1D1F] font-semibold mb-2">Sin gestiones registradas</h4>
              <p className="text-[#AEAEB2] text-sm max-w-xs mx-auto">
                Aún no hay actividades registradas para la etapa de {stageConfig?.name}. 
                Tu equipo de Paliacate te notificará cuando haya avances.
              </p>
            </div>
          ) : (
            <>
              {/* Pipeline Movement History */}
              {history.pipeline_history.length > 0 && (
                <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 rounded-xl p-4 border border-orange-500/20">
                  <h4 className="text-sm font-semibold text-[#1D1D1F] flex items-center gap-2 mb-3">
                    <ArrowRight className="w-4 h-4 text-orange-400" />
                    Movimientos en el Pipeline
                  </h4>
                  <div className="space-y-2">
                    {history.pipeline_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-white/50 p-2 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-[#6E6E73]">
                          Avanzaste a {PIPELINE_STAGES.find(s => s.id === entry.stage)?.name || entry.stage}
                        </span>
                        {entry.timestamp && (
                          <span className="text-xs text-[#AEAEB2] ml-auto">
                            {new Date(entry.timestamp).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Checklist Section */}
              {history.checklist.length > 0 && (
                <div className="bg-white/50 rounded-xl p-4 border border-[rgba(0,0,0,0.06)]">
                  <h4 className="text-sm font-semibold text-[#1D1D1F] flex items-center gap-2 mb-3">
                    <ClipboardList className="w-4 h-4 text-orange-400" />
                    Checklist Completado
                  </h4>
                  <div className="space-y-2">
                    {history.checklist.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {item.done || item.checked ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <Circle className="w-4 h-4 text-[#AEAEB2] flex-shrink-0" />
                        )}
                        <span className={item.done || item.checked ? 'text-green-400' : 'text-[#AEAEB2]'}>
                          {item.label || item.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tasks Section */}
              {history.tasks.length > 0 && (
                <div className="bg-white/50 rounded-xl p-4 border border-[rgba(0,0,0,0.06)]">
                  <h4 className="text-sm font-semibold text-[#1D1D1F] flex items-center gap-2 mb-3">
                    <Camera className="w-4 h-4 text-pink-400" />
                    Tareas y Producción
                  </h4>
                  <div className="space-y-2">
                    {history.tasks.map((task, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-700/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1D1D1F] font-medium truncate">{task.title || task.type}</p>
                          {task.date && (
                            <p className="text-xs text-[#AEAEB2]">
                              {new Date(task.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ml-2 ${
                          task.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          task.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-[#AEAEB2]'
                        }`}>
                          {task.status === 'completed' ? '✓ Hecho' :
                           task.status === 'in_progress' ? 'En curso' : 'Pendiente'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interactions Section */}
              {history.interactions.length > 0 && (
                <div className="bg-white/50 rounded-xl p-4 border border-[rgba(0,0,0,0.06)]">
                  <h4 className="text-sm font-semibold text-[#1D1D1F] flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-blue-400" />
                    Comunicaciones Recientes
                  </h4>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto">
                    {history.interactions.slice(0, 5).map((interaction, idx) => (
                      <div key={idx} className="border-l-2 border-blue-500/30 pl-3 py-1">
                        <p className="text-sm text-[#6E6E73] line-clamp-2">
                          {interaction.notes || interaction.content || interaction.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {interaction.created_at && (
                            <span className="text-xs text-[#AEAEB2]">
                              {new Date(interaction.created_at).toLocaleDateString('es-MX', {
                                day: 'numeric', month: 'short'
                              })}
                            </span>
                          )}
                          {interaction.worker_name && (
                            <span className="text-xs text-blue-400">• {interaction.worker_name}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[rgba(0,0,0,0.06)] bg-white/50">
          <button
            onClick={onClose}
            className="w-full py-2 bg-gray-700 hover:bg-white/60 text-[#1D1D1F] rounded-lg text-sm font-medium transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// Progress timeline component - Mobile Optimized with Click functionality
function ProgressTimeline({ currentStage, clientId }) {
  const [selectedStage, setSelectedStage] = useState(null);
  const currentIndex = PIPELINE_STAGES.findIndex(s => s.id === currentStage);
  
  return (
    <>
      <div className="relative overflow-x-auto pb-2">
        <div className="min-w-[500px] sm:min-w-0">
          {/* Progress bar background */}
          <div className="absolute top-4 sm:top-5 left-0 right-0 h-0.5 sm:h-1 bg-gray-700 rounded-full" />
          
          {/* Progress bar fill */}
          <div 
            className="absolute top-4 sm:top-5 left-0 h-0.5 sm:h-1 bg-gradient-to-r from-orange-500 to-green-500 rounded-full transition-all duration-500"
            style={{ width: `${(currentIndex / (PIPELINE_STAGES.length - 1)) * 100}%` }}
          />
          
          {/* Stage markers */}
          <div className="flex justify-between relative">
            {PIPELINE_STAGES.map((stage, index) => {
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const isPending = index > currentIndex;
              const isClickable = index <= currentIndex; // Can only click completed or current stages
              
              return (
                <div key={stage.id} className="flex flex-col items-center">
                  <button 
                    onClick={() => isClickable && setSelectedStage(stage.id)}
                    disabled={!isClickable}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-lg z-10 transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/30 hover:scale-110 cursor-pointer' 
                        : isCurrent 
                          ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/50 hover:scale-110 cursor-pointer ring-4 ring-orange-500/40 animate-bounce-subtle' 
                          : 'bg-gray-700 text-[#AEAEB2] cursor-not-allowed'
                    }`}
                    style={isCurrent ? {
                      animation: 'pulse-glow 2s infinite, bounce-subtle 1s infinite'
                    } : {}}
                    title={isClickable ? `Ver historial de ${stage.name}` : 'Etapa pendiente'}
                  >
                    {isCompleted ? <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" /> : stage.icon}
                  </button>
                  <span className={`mt-1 sm:mt-2 text-[10px] sm:text-xs font-medium text-center ${
                    isCurrent ? 'text-orange-400' : isCompleted ? 'text-green-400' : 'text-[#AEAEB2]'
                  }`}>
                    {stage.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Hint text */}
        <p className="text-center text-xs text-[#AEAEB2] mt-3">
          Haz clic en una etapa completada para ver su historial
        </p>
      </div>

      {/* Stage History Modal */}
      {selectedStage && (
        <StageHistoryModal
          stage={selectedStage}
          stageConfig={PIPELINE_STAGES.find(s => s.id === selectedStage)}
          clientId={clientId}
          onClose={() => setSelectedStage(null)}
        />
      )}
    </>
  );
}

// Checklist component - Mobile Optimized
function ChecklistSection({ checklist }) {
  if (!checklist || !checklist.items) return null;
  
  return (
    <div className="bg-white/50 rounded-xl p-4 sm:p-6 border border-[rgba(0,0,0,0.06)]">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
          <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
          <span className="hidden sm:inline">Checklist - </span>{checklist.stage_name}
        </h3>
        <span className="text-xs sm:text-sm text-[#AEAEB2]">
          {checklist.completed}/{checklist.total}
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-700 rounded-full mb-4 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-orange-500 to-green-500 rounded-full transition-all duration-500"
          style={{ width: `${checklist.percentage}%` }}
        />
      </div>
      
      {/* Checklist items */}
      <div className="space-y-3">
        {checklist.items.map((item) => (
          <div 
            key={item.id}
            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
              item.checked ? 'bg-green-500/10' : 'bg-gray-700/50'
            }`}
          >
            {item.checked ? (
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-[#AEAEB2] flex-shrink-0" />
            )}
            <span className={item.checked ? 'text-green-300' : 'text-[#6E6E73]'}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Team section with direct contact buttons (WhatsApp and Email)
function TeamSection({ team, businessName }) {
  if (!team || team.length === 0) {
    return (
      <div className="bg-white/50 rounded-xl p-4 sm:p-6 border border-[rgba(0,0,0,0.06)]">
        <h3 className="text-base sm:text-lg font-semibold text-[#1D1D1F] flex items-center gap-2 mb-4">
          <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
          Tu Equipo
        </h3>
        <p className="text-[#AEAEB2] text-sm">Aún no tienes un equipo asignado</p>
      </div>
    );
  }
  
  // Helper to format phone for WhatsApp (remove spaces, dashes, etc)
  const formatWhatsAppNumber = (phone) => {
    if (!phone) return null;
    // Remove all non-numeric characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    // If starts with +, keep it, otherwise assume Mexico country code
    if (!cleaned.startsWith('+')) {
      // If it's a 10-digit number (Mexican format), add +52
      if (cleaned.length === 10) {
        cleaned = '+52' + cleaned;
      }
    }
    return cleaned;
  };
  
  // Generate mailto link with pre-filled subject
  const generateMailtoLink = (email, memberName) => {
    const subject = encodeURIComponent(`Consulta sobre mi cuenta - ${businessName || 'Mi Negocio'}`);
    return `mailto:${email}?subject=${subject}`;
  };
  
  return (
    <div className="bg-white/50 rounded-xl p-4 sm:p-6 border border-[rgba(0,0,0,0.06)]">
      <h3 className="text-base sm:text-lg font-semibold text-[#1D1D1F] flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
        Tu Equipo
      </h3>
      
      <div className="space-y-4">
        {team.map((member, index) => {
          const whatsappNumber = formatWhatsAppNumber(member.phone);
          const hasWhatsApp = !!whatsappNumber;
          const hasEmail = !!member.email;
          
          return (
            <div 
              key={index} 
              className="bg-gray-700/50 rounded-xl p-4 border border-[rgba(0,0,0,0.08)]/50"
              data-testid={`team-member-${index}`}
            >
              {/* Member Info */}
              <div className="flex items-center gap-3 mb-4">
                {/* Profile Photo */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-orange-500/30">
                  {member.photo_url ? (
                    <img 
                      src={member.photo_url} 
                      alt={member.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-full h-full bg-gradient-to-br from-orange-500 to-pink-500 items-center justify-center text-white text-xl font-bold ${member.photo_url ? 'hidden' : 'flex'}`}>
                    {member.name?.charAt(0) || '?'}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1D1D1F] font-semibold text-base sm:text-lg truncate">{member.name}</p>
                  <p className="text-sm text-orange-400 truncate">{member.role || member.role_in_business || 'Encargado'}</p>
                  {member.assigned_to?.length > 0 && (
                    <p className="text-xs text-[#AEAEB2] truncate">
                      {member.assigned_to.map(a => a.business_name).join(', ')}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Contact Buttons - Large and Mobile Friendly */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                {/* WhatsApp Button */}
                {hasWhatsApp && (
                  <a 
                    href={`https://wa.me/${whatsappNumber.replace('+', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-green-600/20 hover:shadow-green-500/30 hover:scale-[1.02] active:scale-[0.98]"
                    data-testid={`whatsapp-btn-${index}`}
                  >
                    <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span className="text-sm sm:text-base">WhatsApp</span>
                  </a>
                )}
                
                {/* Email Button */}
                {hasEmail && (
                  <a 
                    href={generateMailtoLink(member.email, member.name)}
                    className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98]"
                    data-testid={`email-btn-${index}`}
                  >
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span className="text-sm sm:text-base">Email</span>
                  </a>
                )}
                
                {/* Phone Call Button (fallback if no WhatsApp) */}
                {member.phone && !hasWhatsApp && (
                  <a 
                    href={`tel:${member.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-gray-600 hover:bg-gray-500 text-[#1D1D1F] rounded-xl font-semibold transition-all duration-200"
                    data-testid={`phone-btn-${index}`}
                  >
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span className="text-sm sm:text-base">Llamar</span>
                  </a>
                )}
              </div>
              
              {/* No contact info message */}
              {!hasWhatsApp && !hasEmail && !member.phone && (
                <p className="text-center text-[#AEAEB2] text-sm py-2">
                  Información de contacto no disponible
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Upcoming sessions
function UpcomingSection({ sessions }) {
  if (!sessions || sessions.length === 0) {
    return (
      <div className="bg-white/50 rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
        <h3 className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-orange-400" />
          Próximas Sesiones
        </h3>
        <p className="text-[#AEAEB2] text-sm">No hay sesiones programadas</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white/50 rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
      <h3 className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-orange-400" />
        Próximas Sesiones
      </h3>
      
      <div className="space-y-3">
        {sessions.slice(0, 5).map((session, index) => (
          <div key={session.task_id || index} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Clock className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <p className="text-[#1D1D1F] font-medium">{session.title}</p>
                <p className="text-sm text-[#AEAEB2]">
                  {session.scheduled_date} {session.scheduled_time && `- ${session.scheduled_time}`}
                </p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded text-xs ${
              session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
              session.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
              'bg-gray-600 text-[#6E6E73]'
            }`}>
              {session.task_type || 'Sesión'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Main ProgressTab component
export const ProgressTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      
      const response = await axios.get(`${API_URL}/api/client-portal/my-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('No se pudo cargar la información. Intenta de nuevo.');
      toast.error('Error al cargar tu progreso');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-[#AEAEB2]">Cargando tu progreso...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-[#AEAEB2]">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
  
  const { client, checklist, upcoming_sessions, progress, businesses } = dashboardData || {};
  
  // Build team from all businesses
  const team = [];
  const seenWorkers = new Set();
  businesses?.forEach(biz => {
    biz.team?.forEach(member => {
      if (!seenWorkers.has(member.worker_id || member.name)) {
        team.push(member);
        seenWorkers.add(member.worker_id || member.name);
      }
    });
  });
  
  return (
    <div className="space-y-8">
      {/* Header with stats */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-full mb-4">
          <Sparkles className="w-4 h-4 text-orange-400" />
          <span className="text-sm text-orange-300">Tu progreso con Paliacate Studio</span>
        </div>
        
        <h2 className="text-3xl font-bold text-[#1D1D1F] mb-2">
          ¡Hola {client?.name?.split(' ')[0] || 'Cliente'}!
        </h2>
        
        <div className="flex items-center justify-center gap-6 mt-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-400">{progress?.percentage || 0}%</p>
            <p className="text-sm text-[#AEAEB2]">Progreso general</p>
          </div>
          <div className="h-12 w-px bg-gray-700" />
          <div className="text-center">
            <p className="text-3xl font-bold text-green-400">{progress?.completed_tasks || 0}</p>
            <p className="text-sm text-[#AEAEB2]">Tareas completadas</p>
          </div>
          <div className="h-12 w-px bg-gray-700" />
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-400">{progress?.total_tasks || 0}</p>
            <p className="text-sm text-[#AEAEB2]">Tareas totales</p>
          </div>
        </div>
      </div>
      
      {/* Pipeline Timeline */}
      <div className="bg-white/50 rounded-xl p-6 border border-[rgba(0,0,0,0.06)]">
        <h3 className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
          <Target className="w-5 h-5 text-orange-400" />
          Tu posición en el Método Paliacate
        </h3>
        <ProgressTimeline 
          currentStage={client?.pipeline_stage || 'captacion'} 
          clientId={client?.client_id}
        />
      </div>
      
      {/* Main content grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Checklist */}
        <ChecklistSection checklist={checklist} />
        
        {/* Team - pass business name for email subject */}
        <TeamSection team={team} businessName={businesses?.[0]?.name || client?.name} />
      </div>
      
      {/* Upcoming sessions */}
      <UpcomingSection sessions={upcoming_sessions} />
      
      {/* Quick tip */}
      <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 rounded-xl p-6 border border-orange-500/20">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <TrendingUp className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h4 className="text-[#1D1D1F] font-semibold mb-1">¿Tienes dudas?</h4>
            <p className="text-[#AEAEB2] text-sm">
              Tu equipo de Paliacate está disponible para ayudarte. No dudes en contactarnos 
              a través de WhatsApp o email si tienes alguna pregunta sobre tu proyecto.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressTab;
